// Lightweight SI/IP unit conversion + formatting helpers used by the Wizard
// (separate from the legacy SelectionTool helpers to avoid coupling).

export const toSI = {
  temp: (f) => (parseFloat(f) - 32) * 5 / 9,           // °F → °C (absolute)
  tempDelta: (f) => parseFloat(f) * 5 / 9,             // ΔF → ΔC (scale-only — no 32° offset)
  flow: (cfm) => parseFloat(cfm) * 0.4719474,          // CFM → L/s
  waterFlow: (gpm) => parseFloat(gpm) * 0.06309,       // GPM → L/s
  capacity: (btu) => parseFloat(btu) * 0.29307107,     // BTU/h → W
  pressure: (ftH2O) => parseFloat(ftH2O) * 2.989,      // ftH2O → kPa
  staticPressure: (inWG) => parseFloat(inWG) * 248.84, // in. wg → Pa
  length: (ft) => parseFloat(ft) * 0.3048,             // ft → m
  altitude: (ft) => parseFloat(ft) * 0.3048,           // ft → m
};

// Inverse — SI input → IP storage. Used when the global IP/SI toggle is in
// SI mode and the user types values into Rooms / Design Conditions inputs.
export const toIP = {
  temp: (c) => parseFloat(c) * 9 / 5 + 32,             // °C → °F (absolute)
  tempDelta: (c) => parseFloat(c) * 9 / 5,             // ΔC → ΔF (scale-only)
  flow: (ls) => parseFloat(ls) / 0.4719474,            // L/s → CFM
  waterFlow: (ls) => parseFloat(ls) / 0.06309,         // L/s → GPM
  capacity: (w) => parseFloat(w) / 0.29307107,         // W → BTU/h
  pressure: (kpa) => parseFloat(kpa) / 2.989,          // kPa → ftH2O
  staticPressure: (pa) => parseFloat(pa) / 248.84,     // Pa → in. wg
  length: (m) => parseFloat(m) / 0.3048,               // m → ft
  altitude: (m) => parseFloat(m) / 0.3048,             // m → ft
};

// Marketing-rounded unit length: 1 ft = 300 mm (nominal). Used in unit
// names (e.g. "Model L-L-3" in IP → "Model L-L-900" in SI). Matches the
// catalogue's nominal length convention (2ft=600, 3ft=900, etc.) rather
// than the exact 304.8 mm/ft conversion.
export function unitLengthLabel(ft, isSI = false) {
  const n = parseFloat(ft);
  if (!Number.isFinite(n)) return "";
  return isSI ? `${Math.round(n * 300)}` : `${n}`;
}


export const labels = {
  temp: { ip: "°F", si: "°C" },
  tempDelta: { ip: "°F", si: "°C" },
  flow: { ip: "CFM", si: "L/s" },
  waterFlow: { ip: "GPM", si: "L/s" },
  capacity: { ip: "BTU/h", si: "W" },
  pressure: { ip: "ftH₂O", si: "kPa" },
  staticPressure: { ip: "in. wg", si: "Pa" },
  length: { ip: "ft", si: "m" },
};

// Format a number with conversion. v is always stored in IP units; isSI flag
// triggers conversion before display. Returns "—" for null / NaN.
export function fmt(v, kind, isSI = false, decimals = 0) {
  if (v === null || v === undefined || v === "" || isNaN(parseFloat(v))) return "—";
  const num = isSI && toSI[kind] ? toSI[kind](v) : parseFloat(v);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function unitLabel(kind, isSI = false) {
  return labels[kind] ? labels[kind][isSI ? "si" : "ip"] : "";
}

// Pick decimals based on kind in IP/SI
export const defaultDecimals = {
  temp: { ip: 1, si: 1 },
  tempDelta: { ip: 1, si: 1 },
  flow: { ip: 0, si: 1 },
  waterFlow: { ip: 2, si: 3 },
  capacity: { ip: 0, si: 0 },
  pressure: { ip: 2, si: 2 },
  staticPressure: { ip: 3, si: 0 },
  length: { ip: 1, si: 2 },
};

export function fmtKind(v, kind, isSI = false) {
  const d = defaultDecimals[kind] || { ip: 1, si: 1 };
  return fmt(v, kind, isSI, isSI ? d.si : d.ip);
}

// SI-aware input helpers. Stored values are ALWAYS in IP; the UI layer
// converts only at the display/input boundary.
//
//   inputValue(ip, kind, isSI, decimals) → string for an <input value=…>
//   inputToIP(userText, kind, isSI)      → IP-string for setState
//
// `decimals` controls how many digits we keep when echoing the SI form
// back into the input. Defaults to a kind-appropriate value.
export function inputValue(ip, kind, isSI, decimals) {
  if (ip === "" || ip == null) return "";
  const num = parseFloat(ip);
  if (isNaN(num)) return "";
  if (!isSI || !toSI[kind]) return String(ip);
  const d = decimals != null ? decimals : (defaultDecimals[kind]?.si ?? 1);
  return toSI[kind](num).toFixed(d);
}

export function inputToIP(userText, kind, isSI) {
  const trimmed = String(userText ?? "").trim();
  if (trimmed === "") return "";
  const num = parseFloat(trimmed.replace(/[^\d.\-]/g, ""));
  if (isNaN(num)) return "";
  if (!isSI || !toIP[kind]) return String(num);
  // Round to 4 dp to avoid float noise like 8000.0000003
  return String(Math.round(toIP[kind](num) * 10000) / 10000);
}

